// Financiamiento de proyectos — DATOS REALES por cooperativa (banco de proyectos): desembolso
// a un afiliado para un proyecto y registro del retorno de capital y rendimiento. Cada movimiento
// pasa por el motor contable. Antes usaba datos de ejemplo.

import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { Add as AddIcon, Assignment as AssignmentIcon } from '@mui/icons-material';
import EncabezadoOperativo from '../components/EncabezadoOperativo';
import FormularioDialog from '../components/FormularioDialog';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useCooperativaOperativa from '../hooks/useCooperativaOperativa';
import { organizacionApi, proyectosApi, tercerosApi } from '../services/coopnetApi';
import { fecha, fmt, hoy, moneda } from '../utils/formato';

const FinanciamientoProyectos = () => {
  const op = useCooperativaOperativa();
  const id = op.cooperativaId;
  const [dlg, setDlg] = useState(null);
  const [ok, setOk] = useState('');

  const { datos, cargando, error, recargar } = useCargar(async () => {
    if (!id) return null;
    const [proyectos, afiliados, terceros] = await Promise.all([proyectosApi.listar(id), organizacionApi.listarAfiliados(id), tercerosApi.listar(id, 'Afiliado')]);
    return { proyectos, afiliados, terceros };
  }, [id]);

  const proyectos = datos?.proyectos || [];
  const afiliados = useMemo(() => datos?.afiliados || [], [datos]);
  const nombreDe = useMemo(() => new Map(afiliados.map((a) => [a.id, `${a.nombre} ${a.apellido}`])), [afiliados]);
  const terceroDe = (afiliadoId) => (datos?.terceros || []).find((t) => t.afiliadoId === afiliadoId)?.id || `afiliado-${afiliadoId}`;
  const p = dlg?.proyecto;
  const desembolsado = proyectos.reduce((t, x) => t + (x.montoDesembolsado || 0), 0);
  const retornado = proyectos.reduce((t, x) => t + (x.capitalRetornado || 0), 0);

  return (
    <EncabezadoOperativo
      titulo="Financiamiento de proyectos"
      subtitulo="Banco de proyectos de la cooperativa"
      operativa={op}
      acciones={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlg({ tipo: 'nuevo' })}>Desembolsar proyecto</Button>}
    >
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {cargando && !datos ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 2, mb: 3 }}>
            {[['Proyectos', fmt(proyectos.length)], ['Desembolsado', moneda(desembolsado)], ['Capital retornado', moneda(retornado)], ['Por retornar', moneda(desembolsado - retornado)]].map(([t, v]) => (
              <Card key={t}><CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#0D47A11A', color: '#0D47A1', display: 'flex' }}><AssignmentIcon /></Box>
                <Box><Typography variant="h6" fontWeight="bold" sx={{ color: '#0D47A1' }}>{v}</Typography><Typography variant="body2">{t}</Typography></Box>
              </CardContent></Card>
            ))}
          </Box>
          <TablaDatos
            filas={proyectos}
            vacio="Aún no hay proyectos financiados."
            columnas={[
              { titulo: 'Proyecto', render: (x) => x.nombre },
              { titulo: 'Afiliado', render: (x) => nombreDe.get(x.afiliadoId) || '—' },
              { titulo: 'Desembolso', render: (x) => fecha(x.fechaDesembolso) },
              { titulo: 'Desembolsado', align: 'right', render: (x) => moneda(x.montoDesembolsado) },
              { titulo: 'Capital retornado', align: 'right', render: (x) => moneda(x.capitalRetornado) },
              { titulo: 'Por retornar', align: 'right', render: (x) => moneda(x.montoDesembolsado - x.capitalRetornado) },
              { titulo: '', render: (x) => <Button size="small" onClick={() => setDlg({ tipo: 'retorno', proyecto: x })}>Registrar retorno</Button> },
            ]}
          />
        </>
      )}

      <FormularioDialog
        abierto={dlg?.tipo === 'nuevo'}
        titulo="Desembolsar proyecto"
        campos={[
          { campo: 'afiliadoId', label: 'Afiliado', tipo: 'select', requerido: true, opciones: afiliados.filter((a) => a.estado === 'activo').map((a) => ({ valor: a.id, etiqueta: `${a.nombre} ${a.apellido}` })) },
          { campo: 'nombre', label: 'Nombre del proyecto', tipo: 'texto', requerido: true },
          { campo: 'monto', label: 'Monto a desembolsar', tipo: 'numero', requerido: true, positivo: true },
          { campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ fecha: hoy() }}
        textoBoton="Desembolsar"
        onGuardar={async (v) => { await proyectosApi.desembolsar(id, { afiliadoId: v.afiliadoId, terceroId: terceroDe(v.afiliadoId), nombre: v.nombre, monto: v.monto, fecha: v.fecha }); setOk('Proyecto desembolsado.'); recargar(); }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg?.tipo === 'retorno'}
        titulo="Registrar retorno"
        descripcion={p ? `${p.nombre} — por retornar: ${moneda(p.montoDesembolsado - p.capitalRetornado)}` : ''}
        campos={[
          { campo: 'capital', label: 'Capital devuelto', tipo: 'numero', requerido: true },
          { campo: 'rendimiento', label: 'Rendimiento', tipo: 'numero' },
          { campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ fecha: hoy(), rendimiento: 0 }}
        textoBoton="Registrar retorno"
        onGuardar={async (v) => { await proyectosApi.registrarRetorno(id, p.id, { terceroId: terceroDe(p.afiliadoId), capital: v.capital, rendimiento: v.rendimiento, fecha: v.fecha }); setOk('Retorno registrado.'); recargar(); }}
        onClose={() => setDlg(null)}
      />
    </EncabezadoOperativo>
  );
};

export default FinanciamientoProyectos;
